import Model from 'src/app/common/model';

/**
 * @model BusinessCategoryModel
 * @description
 * Represents the data structure for Business Categories within the AquaCloud application.
 * This model serves as a reference entity used to classify different types of business entities
 * participating in the aquaculture ecosystem (e.g., Farmers, Feed Suppliers, Hatcheries).
 *
 * **Purpose:**
 * The primary purpose of this model is to provide a standardized classification system.
 * It allows the application to distinguish between different stakeholders and apply
 * specific business rules, UI layouts, or data validation requirements based on the category.
 *
 * **Key Responsibilities:**
 * 1. **Identification**: Stores the human-readable `name` and system-identifiable `code` for a category.
 * 2. **Contextualization**: Provides a `description` to help users understand the scope of a specific category.
 * 3. **Filtering & Grouping**: Acts as a key for filtering business lists or grouping reports.
 *
 * **Data Flow:**
 * - **Definition**: Categories are typically defined in the backend master data.
 * - **Retrieval**: Fetched by the `BusinessCategoryService` (usually cached or fetched on app load).
 * - **Usage**:
 *   - Used in Registration Forms to allow users to select their business type.
 *   - Used in Search filters to find specific types of businesses.
 *   - Used in `BusinessEntityModel` to link a specific business to its category.
 *
 * **User Roles & Permissions:**
 * - **System Administrators**: Can create, update, or delete business categories (Master Data Management).
 * - **General Users**: Read-only access. They select these categories during sign-up or profile updates.
 *
 * **Features:**
 * - Lightweight DTO extending the base `Model`.
 * - Essential for Role-Based Access Control (RBAC) logic where permissions depend on business category.
 *
 * @extends Model
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
export default class BusinessCategoryModel extends Model {
  /**
   * @type {string}
   * @description
   * The display name of the business category.
   * This is the value shown in UI dropdowns and labels (e.g., "Shrimp Hatchery", "Feed Distributor").
   */
  name: string;

  /**
   * @type {string}
   * @description
   * A unique alphanumeric code representing the category.
   * This code is often used in frontend logic to apply conditional formatting or validation rules
   * without relying on the mutable `name` string.
   * @example "HATCHERY", "FARM_SHRIMP"
   */
  code: string;

  /**
   * @type {string}
   * @description
   * A detailed explanation of what this business category encompasses.
   * Can be used in tooltips or help sections to assist users in selecting the correct category.
   */
  description: string;

  /**
   * @constructor
   * @description
   * Initializes a new instance of the BusinessCategoryModel.
   * Calls the parent `Model` constructor to initialize base properties (like ID, timestamps).
   */
  constructor() {
    super();
  }
}
